import { z } from 'zod';

import { BRAND_BRAIN_SECTIONS } from './fields.js';

// ─── Matching rule ───────────────────────────────────────────────────
// The onboarding half is the tenant's own Nexus record. The resolved tenant
// customer name (`customerPartition`) is matched against the enriched brand
// name; that it equals the onboarding customer_name is an explicit assumption
// pending Data confirmation, not a derived fact. The rule names internal tables,
// so it stays here and is never sent in a response.

/** Brand Brain-specific failures; customer-name and client_context failures use `ClientContextErrorCodes`. */
export const BrandBrainErrorCodes = {
    DUPLICATE_ENRICHED_ROWS: 'BRAND_BRAIN_DUPLICATE_ENRICHED_ROWS',
} as const;

export type BrandBrainErrorCode = (typeof BrandBrainErrorCodes)[keyof typeof BrandBrainErrorCodes];

// ─── API request / event payloads ────────────────────────────────────

export const BrandBrainFetchBodySchema = z.object({
    idempotencyKey: z.string().min(1),
});

export const BrandBrainFetchRequestedPayload = z.object({
    tenantId: z.string().min(1),
    customerName: z.string().min(1),
});

export type BrandBrainFetchRequestedPayload = z.infer<typeof BrandBrainFetchRequestedPayload>;

// ─── Read model ──────────────────────────────────────────────────────

export const BrandBrainFieldStateSchema = z.object({
    value: z.unknown(),
    isGap: z.boolean(),
    visible: z.boolean(),
    /** True for `BRAND_BRAIN_EDITABLE_COLUMNS` and the Nexus-owned fields; Manus-enriched fields are never editable. */
    editable: z.boolean(),
    section: z.enum(BRAND_BRAIN_SECTIONS),
    type: z.enum(['string', 'json', 'numeric']),
});

export type BrandBrainFieldState = z.infer<typeof BrandBrainFieldStateSchema>;

const BrandBrainDomainSchema = z.object({
    exists: z.boolean(),
    fields: z.record(BrandBrainFieldStateSchema),
});

export const BrandBrainReadModelSchema = z.object({
    customerName: z.string(),
    clientContext: BrandBrainDomainSchema,
    enriched: BrandBrainDomainSchema,
    /** Nexus-owned fields, stored on the onboarding record. Absent from a worker that predates them. */
    nexus: BrandBrainDomainSchema.optional(),
});

export type BrandBrainReadModel = z.infer<typeof BrandBrainReadModelSchema>;
